import { HideObjectType } from '../../../../@types/enums';
import { getModuleFillingRecommendation, saveLocalAiResultCache } from '../../../../services/geminiService';
import { useViewerStore } from '../../../../store/store';
import { buildAiScenariosCacheBlobForLocalPersist } from './utils/aiLayoutCacheHydration';

type RunAiFinalizeArgs = {
    apiKey: string;
    scenarios: any[];
    scenariosForTreeScenariosRecord?: any[];
    layoutAtRunStart: string;
    layoutVariantsSummary: string;
    setTreeScenariosByLayoutKey: (next: (prev: Record<string, any[]>) => Record<string, any[]>) => void;
    setAiLoadingStep: (next: string) => void;
    setAiResult: (next: string | null) => void;
    setAiError: (next: string | null) => void;
    setAiLoading: (next: boolean) => void;
    setAiRunElapsedMs: (next: number | null) => void;
    setLayoutKeyWhenTreeScenariosRun: (next: string) => void;
    setIsDashboardOpen: (next: boolean) => void;
    sceneViewer: any;
    startMs: number;
    /** When true, caller finishes loading / opens the dashboard after post-AI layout work. */
    deferCompletion?: boolean;
};

export async function runAiFinalizeAction(args: RunAiFinalizeArgs): Promise<void> {
    const {
        apiKey,
        scenarios,
        scenariosForTreeScenariosRecord,
        layoutAtRunStart,
        layoutVariantsSummary,
        setTreeScenariosByLayoutKey,
        setAiLoadingStep,
        setAiResult,
        setAiError,
        setAiLoading,
        setAiRunElapsedMs,
        setLayoutKeyWhenTreeScenariosRun,
        setIsDashboardOpen,
        sceneViewer,
        startMs,
        deferCompletion = false,
    } = args;

    try {
        const layoutKeyForScenarios = layoutAtRunStart || 'default';
        const recordScenarios = scenariosForTreeScenariosRecord ?? scenarios;
        setTreeScenariosByLayoutKey((prev) => {
            const merged: Record<string, any[]> = {
                ...prev,
                [layoutKeyForScenarios]: [...recordScenarios],
            };
            try {
                (useViewerStore as any).setState({
                    aiModuleFillingTreeScenariosByLayoutKey: merged,
                    aiModuleFillingDesignId: layoutKeyForScenarios,
                });
            } catch {
                // ignore store sync errors
            }
            return merged;
        });

        setAiLoadingStep('Running AI recommendation...');
        const { mapCoordinates, siteAddress: promptSiteAddress } = useViewerStore.getState() as any;
        const latitude = mapCoordinates?.latitude;
        const longitude = mapCoordinates?.longitude;
        useViewerStore.setState({
            geminiModuleFillingLayoutVariantsSummary: layoutVariantsSummary || '',
        } as any);
        const text = await getModuleFillingRecommendation(
            apiKey,
            scenarios,
            latitude,
            longitude,
            promptSiteAddress,
            layoutVariantsSummary || undefined,
        );
        setAiResult(text);
        if (!deferCompletion) {
            const state = useViewerStore.getState() as any;
            void saveLocalAiResultCache(
                {
                    layoutSnapshots: state.layoutSnapshotsByKey ?? null,
                    variantSnapshots: state.aiLayoutVariantSnapshots ?? null,
                    aiScenarios: buildAiScenariosCacheBlobForLocalPersist(),
                    treeCuttingScenarios: state.aiModuleFillingTreeScenariosByLayoutKey ?? {},
                    aiWishlist: [],
                    aiResult: text,
                },
                undefined,
                { force: true },
            );
        }
    } catch (e) {
        if (deferCompletion) {
            // Caller runs the layout pipeline after Gemini; only surface a blocking error if
            // the full pipeline still produces no usable designs.
            throw e;
        }
        setAiError(e instanceof Error ? e.message : String(e));
    } finally {
        if (!deferCompletion) {
            setAiLoading(false);
            setAiLoadingStep('');
            setAiRunElapsedMs(Date.now() - startMs);
            setLayoutKeyWhenTreeScenariosRun(layoutAtRunStart);
            try {
                sceneViewer?.hideObject?.(HideObjectType.GhostTree, (useViewerStore.getState() as any).isHideAllGhostTrees);
            } catch {
                // ignore
            }
            setIsDashboardOpen(true);
        }
    }
}
